import React from 'react';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';

import { Box, Link, Typography, Grid, Card } from '@availity/element';

import DynamicField from '../DynamicField/DynamicField';

const NoteNoSave = () => (
  <Typography sx={{ margin: '0 0 1rem' }}>
    <Typography component="span" sx={{ fontWeight: 'bold' }}>
      Please note:
    </Typography>{' '}
    This tool does not have the ability to temporarily save information for access and final submission at a later
    time. Please complete all required fields in one session.
  </Typography>
);

const ColoradoCommunityManual = () => (
  <Typography sx={{ margin: '0 0 1rem' }}>
    For further information please refer to the Colorado Community Health Alliance Provider Manual.
  </Typography>
);

const MedicaidOnly = () => (
  <DynamicField fieldName="appForMedicaidMembers">
    <Typography sx={{ margin: '0 0 1rem', fontWeight: 'bold' }}>
      This application is for Medicaid Members Only.
    </Typography>
  </DynamicField>
);

const ApplicatioDescription = () => (
  <Box id="landing-page-description">
    <DynamicField fieldName="landingPageDescriptionFlavor1">
      <Typography sx={{ margin: '0 0 1rem' }}>
        Report adverse incidents affecting any{' '}
        <Typography component="span">
          <DynamicField fieldName="payerNameForOH">
            <Typography component="span">Anthem</Typography>
          </DynamicField>
          <DynamicField fieldName="payerNameForKS">
            <Typography component="span">Healthy Blue</Typography>
          </DynamicField>
        </Typography>{' '}
        {`member that occurs either during delivery of health care services or in the member's personal life.`}
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        {`If you have questions regarding the report or need to update a report you've previously submitted, please contact our Provider Services.`}
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        For further information about incident reporting, please refer to the{' '}
        <Typography component="span">
          <DynamicField fieldName="providerManualNameForOH">
            <Typography component="span">Ohio Anthem </Typography>
          </DynamicField>
          <DynamicField fieldName="providerManualNameForKS">
            <Typography component="span">Kansas </Typography>
          </DynamicField>
          Provider Manual.
        </Typography>
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        Please note: This tool does not have the ability to temporarily save information for access and final submission
        at a later time. Please complete all required fields in one session.
      </Typography>
      <Typography variant="h6" sx={{ m: '40px 0', fontWeight: 'bold' }}>
        Complete the following to get started:
      </Typography>
    </DynamicField>
    <DynamicField fieldName="landingPageDescriptionMyCareOH">
      <Typography sx={{ margin: '0 0 1rem' }}>
        {`Report incidents affecting any Anthem Ohio MyCare member that occurs during delivery of services or in the member's personal life.`}
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        {`If you have questions regarding the report or need to update a report you've previously submitted, contact our Incident Management team at `}
        <Link href="mailto:MyCareIncidents@anthem.com">MyCareIncidents@anthem.com</Link>.
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        Please note: This reporting tool does not have the ability to temporarily save information for access and final
        submission at a later time. Please complete all required fields in one session.
      </Typography>
      <Typography variant="h6" sx={{ m: '40px 0', fontWeight: 'bold' }}>
        Complete the following to get started:
      </Typography>
    </DynamicField>
    <DynamicField fieldName="landingPageDescriptionFlavor2">
      <Typography sx={{ margin: '0 0 1rem' }}>
        Report adverse incidents affecting any Healthy Blue member that occurs during delivery of health care services.
        Adverse incidents may result in physical, psychological, or financial harm.
      </Typography>

      <Typography sx={{ margin: '0 0 1rem', fontWeight: 'bold' }}>
        If you have questions regarding the report or need to update a report you’ve previously submitted, please
        contact our Quality Management Department at{' '}
        <DynamicField fieldName="mailForNEMarket">
          <Link href="mailto:QualityManagementPQOCTeam@anthem.com">QualityManagementPQOCTeam@anthem.com</Link>
        </DynamicField>
        <DynamicField fieldName="mailForMOMarket">
          <Link href="mailto:MO.HPGA@anthem.com">MO.HPGA@anthem.com</Link>
        </DynamicField>
        <DynamicField fieldName="mailForNCMarket">
          <Link href="mailto:Qualityofcare@healthybluenc.com">Qualityofcare@healthybluenc.com</Link>
        </DynamicField>
      </Typography>

      <Typography sx={{ margin: '0 0 1rem' }}>
        <Typography component="span" sx={{ fontWeight: 'bold' }}>
          Please note:
        </Typography>{' '}
        This tool does not have the ability to temporarily save information for access and final submission at a later
        time. Please complete all required fields in one session.
      </Typography>

      <Typography sx={{ margin: '0 0 1rem' }}>
        For further information please refer to the Healthy Blue Provider Manual.
      </Typography>

      <DynamicField fieldName="appForMedicaidMembers">
        <Typography sx={{ margin: '0 0 1rem' }}>
          <Typography component="span" sx={{ fontWeight: 'bold' }}>
            This application is for Medicaid Members Only.
          </Typography>
        </Typography>
      </DynamicField>
    </DynamicField>
	<DynamicField fieldName="landingPageDescriptionFlavor3">
      <Typography sx={{ margin: '0 0 1rem' }}>
        Report quality of care concerns affecting any Colorado Community Health Alliance member that occurs during delivery of health care services. Quality of care events may result in physical, psychological, or financial harm.
      </Typography>
      <NoteNoSave />
      <ColoradoCommunityManual />
      <MedicaidOnly /> 
    </DynamicField>
    <DynamicField fieldName="landingPageDescriptionFlavor4">
      <Typography sx={{ margin: '0 0 1rem' }}>
        Report all Critical incidents affecting any{' '}
        <DynamicField fieldName="payerNameForAnthem">
          <Typography component="span">
            Anthem
          </Typography>
        </DynamicField>
        {' '}Member as soon as you are aware and no later than one business day.
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        If you have questions regarding completion of report please contact Provider Services. If you have immediate information you need to discuss about the member please contact the members care team.
      </Typography>
      <Typography sx={{ margin: '0 0 1rem' }}>
        For further information about critical incident reporting, please refer to the{' '}
        <DynamicField fieldName="providerManualNameForWI">
          <Typography component="span">
            Wisconsin Family Care Provider Manual
          </Typography>
        </DynamicField>
        {' '}or your{' '}
        <DynamicField fieldName="payerNameForAnthem">
          <Typography component="span">
            Anthem
          </Typography>
        </DynamicField>
        {' '}Provider Family Care Contract addendum.
      </Typography>
      <NoteNoSave/>
       <Typography variant="h6" sx={{ m: '40px 0', fontWeight: 'bold' }}>
        Complete the following to get started:
      </Typography>
    </DynamicField>
    <DynamicField fieldName="RMIRFDescription">
      <Card sx={{ p: 3, mt: 2, mb: 2, backgroundColor: '#e6e6e6ff' }} id="health-plan-card">
        <Grid id="rmirf-desc-grid">
          <Typography id="rmirf-desc-main">
            Use this form to report adverse incidents member injuries that
            occur during the delivery of Managed Care Plan covered services
            and incidents of suspected abuse, neglect and exploitation.
          </Typography>
          <Typography mt={2} id="rmirf-desc-contact">
            If you have questions regarding the form or need to update a previously submitted form,
            please contact Risk Management at (813-523-0992 or 305-335-8420).
          </Typography>
          <Typography mt={2} id="rmirf-desc-note">
            Please note: This tool does not have the ability to save entered date
            and retrieve it at a later date.
          </Typography>
          <Typography variant="h6" mt={4} id="rmirf-desc-confidential">
            Pursuant To F.S 395.0197 and 641.55 THIS REPORT IS CONFIDENTIAL DO NOT COPY
          </Typography>
          <Typography mt={4} id="rmirf-desc-date">
            Date Form Received In Plan RM Dept: <Typography id="rmirf-desc-date-value" component="span" sx={{ fontWeight: 'bold' }}>{dayjs().format('MM/DD/YYYY')}</Typography>
          </Typography>
          <Typography mt={4} id="rmirf-desc-record-number">
            Record Number:
          </Typography>
        </Grid>
      </Card>
    </DynamicField>
  </Box>
);

export default observer(ApplicatioDescription);
